import { createPortal } from 'react-dom';
import { useState } from 'react';
import styles from './ActorDetailModal.module.css';
import { brandConfig } from '../../config/brands';
import {
  getActorGenderLabel,
  getEthnicityLabel,
  getFaceScanStatusLabel,
  isCharacterActor,
  type FaceScanStatus,
} from '../../features/actors/actorAttributes';

const SUPABASE_URL = 'https://lirsarznxaidebqhgxch.supabase.co';

export type ActorInfo = {
  id: string;
  name: string;
  display_name: string;
  display_name_en?: string;
  // キャラクター (ethnicity='character') は性別未指定 (null) がありうる
  gender: 'male' | 'female' | null;
  min_age: number | null;
  max_age: number | null;
  ethnicity: string | null;
  tags: string[];
  character_sheet_bucket: string | null;
  character_sheet_path: string | null;
  face_image_bucket: string | null;
  face_image_path: string | null;
  // face-safe 検査状態 (キャラクター俳優のみ意味を持つ。BE は select('*') で返す)
  character_sheet_face_scan_status?: FaceScanStatus;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  actor: ActorInfo | null;
  onEdit?: (actor: ActorInfo) => void;
};

export function ActorDetailModal({ isOpen, onClose, actor, onEdit }: Props) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const handleEdit = () => {
    if (actor && onEdit) {
      onEdit(actor);
      onClose();
    }
  };

  if (!isOpen || !actor) return null;

  const characterSheetUrl = actor.character_sheet_bucket && actor.character_sheet_path
    ? `${SUPABASE_URL}/storage/v1/object/public/${actor.character_sheet_bucket}/${actor.character_sheet_path}`
    : null;

  const faceImageUrl = actor.face_image_bucket && actor.face_image_path
    ? `${SUPABASE_URL}/storage/v1/object/public/${actor.face_image_bucket}/${actor.face_image_path}`
    : null;

  const ethnicityLabel = getEthnicityLabel(actor.ethnicity) ?? '指定なし';

  return createPortal(
    <>
      <div className={styles.overlay} onClick={onClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          {/* ヘッダー */}
          <div className={styles.header}>
            <h2 className={styles.title}>{brandConfig.labels.aiActor}・{actor.display_name}</h2>
            <div className={styles.headerActions}>
              {onEdit && (
                <button type="button" className={styles.editButton} onClick={handleEdit}>
                  編集
                </button>
              )}
              <button type="button" className={styles.closeButton} onClick={onClose}>
                ×
              </button>
            </div>
          </div>

          {/* コンテンツ */}
          <div className={styles.content}>
            {/* 左: 画像 */}
            <div className={styles.imageSection}>
              {faceImageUrl && (
                <div
                  className={styles.faceImage}
                  onClick={() => setLightboxImage(faceImageUrl)}
                >
                  <img src={faceImageUrl} alt={actor.display_name} />
                </div>
              )}
              {characterSheetUrl && (
                <div
                  className={styles.characterSheet}
                  onClick={() => setLightboxImage(characterSheetUrl)}
                >
                  <img src={characterSheetUrl} alt="キャラクターシート" />
                </div>
              )}
            </div>

            {/* 右: 情報 */}
            <div className={styles.infoSection}>
              <dl className={styles.infoList}>
                <div className={styles.infoItem}>
                  <dt>内部名</dt>
                  <dd><code>{actor.name}</code></dd>
                </div>
                <div className={styles.infoItem}>
                  <dt>性別</dt>
                  <dd>{getActorGenderLabel(actor.gender)}</dd>
                </div>
                {(actor.min_age || actor.max_age) && (
                  <div className={styles.infoItem}>
                    <dt>年齢</dt>
                    <dd>
                      {actor.min_age && actor.max_age
                        ? `${actor.min_age}〜${actor.max_age}歳`
                        : actor.min_age
                        ? `${actor.min_age}歳〜`
                        : `〜${actor.max_age}歳`}
                    </dd>
                  </div>
                )}
                <div className={styles.infoItem}>
                  <dt>人種</dt>
                  <dd>{ethnicityLabel}</dd>
                </div>
                {/* face-safe 検査状態 (キャラクター俳優のみ表示。人間俳優には概念が適用されない) */}
                {isCharacterActor(actor) && (
                  <div className={styles.infoItem}>
                    <dt>顔検査</dt>
                    <dd>{getFaceScanStatusLabel(actor.character_sheet_face_scan_status)}</dd>
                  </div>
                )}
                {actor.tags && actor.tags.length > 0 && (
                  <div className={styles.infoItem}>
                    <dt>{brandConfig.labels.tag}</dt>
                    <dd>
                      <div className={styles.tags}>
                        {actor.tags.map((tag, i) => (
                          <span key={i} className={styles.tag}>{tag}</span>
                        ))}
                      </div>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* ライトボックス */}
      {lightboxImage && (
        <div className={styles.lightboxOverlay} onClick={() => setLightboxImage(null)}>
          <button
            type="button"
            className={styles.lightboxClose}
            onClick={() => setLightboxImage(null)}
          >
            ×
          </button>
          <img
            src={lightboxImage}
            alt="拡大画像"
            className={styles.lightboxImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>,
    document.body
  );
}
